import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router'
import { templatesApiService } from '../services/templatesApiService'
import { foldersApiService } from '../services/foldersApiService'
import { renderApiService } from '../services/renderApiService'
import { samplesApiService, type TemplateSample } from '../services/samplesApiService'
import { integrationsApiService, type OAuthConnection } from '../services/integrationsApiService'
import { ApiError } from '../services/BaseApiService'
import { usePreviewRender } from '../hooks/usePreviewRender'
import { useDebounce } from '../hooks/useDebounce'
import { useIsMobile } from '../hooks/useMediaQuery'
import type { Folder, Template, TemplateVersion, TemplateVariable, PayloadFormat } from '../types'
import { buildJsonSkeleton } from '../utils/payloadSkeleton'
import PayloadEditor, { type PayloadEditorHandle } from '../components/PayloadEditor'
import PreviewPane from '../components/PreviewPane'
import { colors, fontFamily, fontSize, radius, shadow, spacing } from '../styles/theme'
import Toggle from '../components/Toggle'
import { ConfirmModal, Select, useDialogs } from '../components/ui'
import { ancestorChain } from '../utils/folderTree'
import { usePublishBreadcrumb } from '../contexts/BreadcrumbContext'

export default function TemplatePreviewPage() {
  const dialogs = useDialogs()
  const { templateId } = useParams<{ templateId: string }>()
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const [template, setTemplate] = useState<Template | null>(null)
  // Folder chain for the top-bar breadcrumb; templates/get returns only a folderId.
  const [folders, setFolders] = useState<Folder[]>([])
  const [versions, setVersions] = useState<TemplateVersion[]>([])
  const [selectedVersion, setSelectedVersion] = useState<TemplateVersion | null>(null)
  const [variables, setVariables] = useState<TemplateVariable[]>([])
  // The version whose variables and samples are loaded. PayloadEditor seeds its state once, on mount,
  // so it is mounted only after this matches the selected version (and re-keyed per version).
  const [loadedVersionId, setLoadedVersionId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState<'editor' | 'preview'>('editor')
  const [samples, setSamples] = useState<TemplateSample[]>([])
  const [saveName, setSaveName] = useState('')
  const [saving, setSaving] = useState(false)
  const [sampleError, setSampleError] = useState('')
  const [pendingDelete, setPendingDelete] = useState<TemplateSample | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [emailTo, setEmailTo] = useState('')
  const [sending, setSending] = useState(false)
  const [sendStatus, setSendStatus] = useState<'idle' | 'sent' | 'error'>('idle')
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvRunning, setCsvRunning] = useState(false)
  const [csvResults, setCsvResults] = useState<Array<{ rowIndex: number; renderJobId: string; success: boolean; errorMessage: string | null }> | null>(null)
  const [xlsxFile, setXlsxFile] = useState<File | null>(null)
  const [xlsxRunning, setXlsxRunning] = useState(false)
  const [xlsxResults, setXlsxResults] = useState<Array<{ rowIndex: number; renderJobId: string; success: boolean; errorMessage: string | null }> | null>(null)
  const [outputFormat, setOutputFormat] = useState<'pdf' | 'docx' | 'xlsx' | 'pptx'>('pdf')
  const [deliverToUrl, setDeliverToUrl] = useState('')
  const [delivering, setDelivering] = useState(false)
  const [deliverStatus, setDeliverStatus] = useState<'idle' | 'sent' | 'error'>('idle')
  const [onMissing, setOnMissing] = useState<'blank' | 'error' | 'hide'>('blank')
  const [watermark, setWatermark] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [currentPayload, setCurrentPayload] = useState('{}')
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [shareTtl, setShareTtl] = useState(24)
  const [sharePassword, setSharePassword] = useState('')
  const [shareMaxViews, setShareMaxViews] = useState('')
  const [sharing, setSharing] = useState(false)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [cloudConnections, setCloudConnections] = useState<OAuthConnection[]>([])
  const [cloudConnectionId, setCloudConnectionId] = useState('')
  const [cloudFolderId, setCloudFolderId] = useState('')
  const [cloudFileName, setCloudFileName] = useState('')
  const [cloudDelivering, setCloudDelivering] = useState(false)
  const [cloudDeliverStatus, setCloudDeliverStatus] = useState<'idle' | 'sent' | 'error'>('idle')
  const [cloudResultUrl, setCloudResultUrl] = useState<string | null>(null)
  const editorRef = useRef<PayloadEditorHandle>(null)

  useEffect(() => {
    foldersApiService.listAll().then(setFolders).catch(() => setFolders([]))
  }, [])

  // Templates › folder1 › sub2 › <template name> › Preview. The template name links back to its
  // detail page; only "Preview" is the leaf.
  usePublishBreadcrumb(useMemo(() => {
    if (!template) return null
    return [
      { label: 'Templates', to: '/app/templates' },
      ...ancestorChain(folders, template.folderId).map(f => ({
        label: f.name,
        to: `/app/templates?folderId=${f.folderId}`,
      })),
      { label: template.name, to: `/app/templates/${template.templateId}` },
      { label: 'Preview' },
    ]
  }, [template, folders]))

  const previewFn = useCallback(
    (payload: string, format: 'json' | 'xml', signal: AbortSignal) =>
      renderApiService.preview(selectedVersion!.templateVersionId, payload, format, { signal, onMissing, outputFormat }),
    [selectedVersion, onMissing, outputFormat]
  )

  const { pdfUrl, loading: pdfLoading, error: pdfError, trigger } = usePreviewRender(
    selectedVersion?.templateVersionId ?? null,
    previewFn
  )

  useEffect(() => {
    integrationsApiService.listConnections().then(conns => {
      setCloudConnections(conns.filter(c => c.provider === 'google'))
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (!templateId) return
    Promise.all([
      templatesApiService.getById(templateId),
      templatesApiService.listVersions(templateId),
    ]).then(([t, v]) => {
      setTemplate(t)
      setVersions(v)
      if (v.length > 0) {
        const latest = v.reduce((a, b) => a.versionNumber > b.versionNumber ? a : b)
        setSelectedVersion(latest)
      }
    }).catch(err => {
      setError(err instanceof ApiError ? err.message : 'Failed to load template')
    }).finally(() => setLoading(false))
  }, [templateId])

  useEffect(() => {
    if (!selectedVersion) return
    const versionId = selectedVersion.templateVersionId
    let cancelled = false
    setLoadedVersionId(null)
    Promise.all([
      templatesApiService.getVariables(versionId).catch(() => [] as TemplateVariable[]),
      samplesApiService.list(versionId).catch(() => [] as TemplateSample[]),
    ]).then(([vars, samps]) => {
      if (cancelled) return
      setVariables(vars)
      setSamples(samps)
      // The editor mounts with this skeleton but does not report it, so seed the page's copy too.
      setCurrentPayload(buildJsonSkeleton(vars))
      setLoadedVersionId(versionId)
    })
    return () => { cancelled = true }
  }, [selectedVersion])

  async function handleSaveSample() {
    if (!saveName.trim() || !selectedVersion) return
    setSaving(true)
    setSampleError('')
    try {
      const payload = editorRef.current?.getPayload() ?? '{}'
      const saved = await samplesApiService.save(selectedVersion.templateVersionId, saveName.trim(), payload)
      setSamples(prev => [saved, ...prev])
      setSaveName('')
    } catch (err) {
      setSampleError(err instanceof ApiError ? err.message : 'Failed to save sample')
    } finally {
      setSaving(false)
    }
  }

  async function confirmDeleteSample() {
    const s = pendingDelete
    if (!s || !selectedVersion) return
    setDeleting(true)
    setSampleError('')
    try {
      await samplesApiService.delete(s.sampleId, selectedVersion.templateVersionId)
      setSamples(prev => prev.filter(x => x.sampleId !== s.sampleId))
    } catch (err) {
      setSampleError(err instanceof ApiError ? err.message : 'Failed to delete sample')
    } finally {
      setDeleting(false)
      setPendingDelete(null)
    }
  }

  const handleRender = useCallback((payload: string, format: PayloadFormat) => {
    trigger(payload, format)
    if (isMobile) setActiveTab('preview')
  }, [trigger, isMobile])

  const debouncedPayload = useDebounce(currentPayload, 800)
  const payloadFormat = useMemo<PayloadFormat>(() => {
    try { JSON.parse(debouncedPayload); return 'json' } catch { return 'json' }
  }, [debouncedPayload])

  useEffect(() => {
    if (!autoRefresh || !selectedVersion) return
    if (editorRef.current?.hasErrors()) return
    trigger(debouncedPayload, payloadFormat)
  }, [autoRefresh, debouncedPayload, selectedVersion]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDeliver() {
    const payload = editorRef.current?.getPayload()
    if (!payload || !selectedVersion || !deliverToUrl.trim()) return
    setDelivering(true)
    setDeliverStatus('idle')
    try {
      await renderApiService.generate(selectedVersion.templateVersionId, payload, {
        outputFormat, onMissing, watermark, deliverToUrl: deliverToUrl.trim(),
      })
      setDeliverStatus('sent')
      setTimeout(() => setDeliverStatus('idle'), 4000)
    } catch {
      setDeliverStatus('error')
      setTimeout(() => setDeliverStatus('idle'), 4000)
    } finally {
      setDelivering(false)
    }
  }

  async function handleCloudDeliver() {
    const payload = editorRef.current?.getPayload()
    if (!payload || !selectedVersion || !cloudConnectionId) return
    setCloudDelivering(true)
    setCloudDeliverStatus('idle')
    setCloudResultUrl(null)
    try {
      const result = await renderApiService.generateToCloud(selectedVersion.templateVersionId, payload, {
        outputFormat, onMissing, watermark,
        cloudStorageConnectionId: cloudConnectionId,
        cloudStoragePath: cloudFolderId.trim() || undefined,
        cloudStorageFileName: cloudFileName.trim() || undefined,
      })
      setCloudResultUrl(result.cloudFileUrl ?? null)
      setCloudDeliverStatus('sent')
      setTimeout(() => setCloudDeliverStatus('idle'), 6000)
    } catch {
      setCloudDeliverStatus('error')
      setTimeout(() => setCloudDeliverStatus('idle'), 4000)
    } finally {
      setCloudDelivering(false)
    }
  }

  async function handleSendEmail() {
    const payload = editorRef.current?.getPayload()
    if (!payload || !selectedVersion || !emailTo.trim()) return
    setSending(true)
    setSendStatus('idle')
    try {
      await renderApiService.generate(selectedVersion.templateVersionId, payload, {
        outputFormat, onMissing, watermark, emailTo: emailTo.trim(),
      })
      setSendStatus('sent')
      setTimeout(() => setSendStatus('idle'), 4000)
    } catch {
      setSendStatus('error')
      setTimeout(() => setSendStatus('idle'), 4000)
    } finally {
      setSending(false)
    }
  }

  async function handleBatchCsv() {
    if (!csvFile || !selectedVersion) return
    setCsvRunning(true)
    setCsvResults(null)
    try {
      const results = await renderApiService.batchCsv(selectedVersion.templateVersionId, csvFile, { outputFormat, onMissing })
      setCsvResults(results)
    } catch (err) {
      setCsvResults([{ rowIndex: -1, renderJobId: '', success: false, errorMessage: err instanceof Error ? err.message : 'Batch failed' }])
    } finally {
      setCsvRunning(false)
    }
  }

  async function handleBatchXlsx() {
    if (!xlsxFile || !selectedVersion) return
    setXlsxRunning(true)
    setXlsxResults(null)
    try {
      const results = await renderApiService.batchXlsx(selectedVersion.templateVersionId, xlsxFile, { outputFormat, onMissing })
      setXlsxResults(results)
    } catch (err) {
      setXlsxResults([{ rowIndex: -1, renderJobId: '', success: false, errorMessage: err instanceof Error ? err.message : 'Batch failed' }])
    } finally {
      setXlsxRunning(false)
    }
  }

  async function handleSharePreview() {
    const payload = editorRef.current?.getPayload()
    if (!payload || !selectedVersion) return
    setSharing(true)
    setShareUrl(null)
    try {
      const result = await renderApiService.createPreviewShare(
        selectedVersion.templateVersionId,
        payload,
        {
          outputFormat,
          ttlHours: shareTtl,
          password: sharePassword.trim() || undefined,
          maxViews: shareMaxViews ? parseInt(shareMaxViews, 10) : undefined,
        }
      )
      setShareUrl(result.shareUrl)
    } catch (err) {
      void dialogs.alert(err instanceof Error ? err.message : 'Share failed')
    } finally {
      setSharing(false)
    }
  }

  async function handleDownload() {
    const payload = editorRef.current?.getPayload()
    if (!payload || !selectedVersion) return
    setDownloading(true)
    try {
      const blob = await renderApiService.generate(selectedVersion.templateVersionId, payload, { outputFormat, onMissing, watermark })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${template?.name ?? 'document'}.${outputFormat}`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      void dialogs.alert(err instanceof Error ? err.message : 'Download failed')
    } finally {
      setDownloading(false)
    }
  }

  if (loading) {
    return <div style={{ padding: spacing.xl, color: colors.textSecondary }}>Loading…</div>
  }

  if (error) {
    return <div style={{ padding: spacing.xl, color: colors.error }}>{error}</div>
  }

  return (
    <>
    <div style={{ fontFamily: fontFamily.sans, display: 'flex', flexDirection: 'column', height: '100%', gap: spacing.md }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md, flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate(`/app/templates/${templateId}`)}
          style={{
            background: 'transparent',
            border: 'none',
            color: colors.primary,
            cursor: 'pointer',
            fontSize: fontSize.sm,
            padding: 0,
          }}
        >
          ← Back
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: fontSize.xl, fontWeight: 700, color: colors.text }}>
            {template?.name ?? 'Preview'}
          </h1>
          {versions.length > 1 && (
            <div style={{ marginTop: '4px', width: 140 }}>
              <Select
                inputSize="sm"
                value={selectedVersion?.templateVersionId ?? ''}
                onChange={v => {
                  const found = versions.find(ver => ver.templateVersionId === v)
                  if (found) setSelectedVersion(found)
                }}
                options={versions.map(v => ({ value: v.templateVersionId, label: `v${v.versionNumber}` }))}
              />
            </div>
          )}
        </div>

        {/* Output format + download */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: 'auto' }}>
          {(['pdf', 'docx', 'xlsx', 'pptx'] as const).map(fmt => (
            <button
              key={fmt}
              onClick={() => setOutputFormat(fmt)}
              style={{
                padding: '5px 10px',
                border: `1px solid ${outputFormat === fmt ? colors.primary : colors.border}`,
                borderRadius: radius.sm,
                background: outputFormat === fmt ? colors.primary : 'none',
                color: outputFormat === fmt ? colors.onPrimary : colors.text,
                fontSize: fontSize.sm,
                fontWeight: outputFormat === fmt ? 600 : 400,
                cursor: 'pointer',
                textTransform: 'uppercase',
              }}
            >
              {fmt}
            </button>
          ))}
          <button
            onClick={handleDownload}
            disabled={downloading}
            style={{
              padding: '5px 14px',
              background: downloading ? colors.textMuted : colors.success,
              color: colors.onPrimary,
              border: 'none',
              borderRadius: radius.sm,
              fontSize: fontSize.sm,
              fontWeight: 600,
              cursor: downloading ? 'default' : 'pointer',
              marginLeft: '4px',
            }}
          >
            {downloading ? '…' : `Download ${outputFormat.toUpperCase()}`}
          </button>
          <button
            onClick={() => { setShareModalOpen(true); setShareUrl(null) }}
            style={{
              padding: '5px 14px',
              background: 'none',
              color: colors.primary,
              border: `1px solid ${colors.primary}`,
              borderRadius: radius.sm,
              fontSize: fontSize.sm,
              fontWeight: 600,
              cursor: 'pointer',
              marginLeft: '4px',
            }}
          >
            Share Preview
          </button>
        </div>
      </div>

      {/* Mobile tab bar */}
      {isMobile && (
        <div style={{ display: 'flex', border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'hidden' }}>
          {(['editor', 'preview'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1,
                padding: spacing.sm,
                border: 'none',
                background: activeTab === tab ? colors.primary : colors.surface,
                color: activeTab === tab ? colors.onPrimary : colors.textSecondary,
                fontWeight: activeTab === tab ? 600 : 400,
                fontSize: fontSize.base,
                cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {tab}
            </button>
          ))}
        </div>
      )}

      {/* Main content */}
      <div style={{
        flex: 1,
        display: isMobile ? 'block' : 'grid',
        gridTemplateColumns: isMobile ? undefined : '380px 1fr',
        gap: spacing.lg,
        minHeight: 0,
        overflow: isMobile ? 'auto' : 'hidden',
      }}>
        {/* Editor panel */}
        <div style={{
          display: isMobile && activeTab !== 'editor' ? 'none' : 'flex',
          flexDirection: 'column',
          gap: spacing.md,
          overflowY: isMobile ? undefined : 'auto',
          padding: isMobile ? `0 0 ${spacing.xl}` : undefined,
        }}>
          {/* Saved samples */}
          {(samples.length > 0 || true) && (
            <div style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: radius.md,
              padding: spacing.md,
              boxShadow: shadow.sm,
            }}>
              <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: spacing.sm }}>
                Saved Samples
              </div>
              {samples.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: spacing.sm }}>
                  {samples.map(s => (
                    <div key={s.sampleId} style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                      <button
                        onClick={() => editorRef.current?.loadPayload(s.payloadJson)}
                        style={{
                          flex: 1,
                          textAlign: 'left',
                          padding: '4px 8px',
                          background: 'none',
                          border: `1px solid ${colors.border}`,
                          borderRadius: radius.sm,
                          fontSize: fontSize.sm,
                          color: colors.text,
                          cursor: 'pointer',
                        }}
                      >
                        {s.name}
                      </button>
                      <button
                        onClick={() => setPendingDelete(s)}
                        aria-label={`Delete sample ${s.name}`}
                        style={{
                          padding: '4px 8px',
                          background: 'none',
                          border: `1px solid ${colors.errorBorder}`,
                          borderRadius: radius.sm,
                          fontSize: fontSize.sm,
                          color: colors.error,
                          cursor: 'pointer',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: fontSize.sm, color: colors.textMuted, marginBottom: spacing.sm }}>
                  No saved samples yet.
                </div>
              )}
              {sampleError && (
                <div role="alert" style={{ fontSize: fontSize.sm, color: colors.error, marginBottom: spacing.sm }}>
                  {sampleError}
                </div>
              )}
              <div style={{ display: 'flex', gap: spacing.xs }}>
                <input
                  value={saveName}
                  onChange={e => setSaveName(e.target.value)}
                  placeholder="Sample name…"
                  onKeyDown={e => e.key === 'Enter' && handleSaveSample()}
                  style={{
                    flex: 1,
                    padding: '4px 8px',
                    border: `1px solid ${colors.border}`,
                    borderRadius: radius.sm,
                    fontSize: fontSize.sm,
                    outline: 'none',
                  }}
                />
                <button
                  onClick={handleSaveSample}
                  disabled={!saveName.trim() || saving}
                  style={{
                    padding: '4px 12px',
                    background: saveName.trim() ? colors.primary : colors.textMuted,
                    color: colors.onPrimary,
                    border: 'none',
                    borderRadius: radius.sm,
                    fontSize: fontSize.sm,
                    cursor: saveName.trim() ? 'pointer' : 'default',
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          )}

          <div style={{
            background: colors.surface,
            borderRadius: radius.md,
            padding: spacing.lg,
            boxShadow: shadow.sm,
          }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: spacing.xs }}>
              <Toggle
                checked={autoRefresh}
                onChange={setAutoRefresh}
                label={<span style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>Auto-refresh</span>}
              />
            </div>
            {selectedVersion && loadedVersionId === selectedVersion.templateVersionId ? (
              <PayloadEditor key={loadedVersionId} ref={editorRef} variables={variables} onChange={setCurrentPayload} onRender={handleRender} renderLoading={pdfLoading} />
            ) : (
              <div style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>Loading variables…</div>
            )}
          </div>

          {/* Tag validation panel */}
          {variables.length > 0 && (
            <div style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: radius.md,
              padding: spacing.md,
              boxShadow: shadow.sm,
            }}>
              <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: spacing.sm }}>
                Variables ({variables.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', maxHeight: 200, overflowY: 'auto' }}>
                {variables.map(v => {
                  let found = false
                  try {
                    const obj = JSON.parse(currentPayload)
                    const segments = v.normalizedKey.split('.')
                    let cur: unknown = obj
                    for (const seg of segments) {
                      if (cur == null || typeof cur !== 'object') { cur = undefined; break }
                      cur = (cur as Record<string, unknown>)[seg]
                    }
                    found = cur !== undefined && cur !== null && cur !== ''
                  } catch { /* invalid JSON */ }
                  return (
                    <div
                      key={v.normalizedKey}
                      onClick={() => editorRef.current?.insertVariable(v.rawName)}
                      title="Click to insert at cursor"
                      style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, cursor: 'pointer', borderRadius: radius.sm, padding: '1px 4px', margin: '0 -4px' }}
                      onMouseEnter={e => (e.currentTarget.style.background = colors.background)}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <span style={{ color: found ? colors.success : colors.error, fontSize: fontSize.sm, minWidth: 14 }}>
                        {found ? '✓' : '✗'}
                      </span>
                      <code style={{ fontSize: fontSize.xs, color: found ? colors.text : colors.textMuted }}>
                        {'{{'}{v.rawName}{'}}'}
                      </code>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Render options */}
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            padding: spacing.md,
            boxShadow: shadow.sm,
          }}>
            <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: spacing.sm }}>
              Render Options
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, cursor: 'pointer' }}>
                <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, minWidth: 90 }}>
                  On missing var:
                </div>
                <div style={{ display: 'flex', gap: '2px' }}>
                  {(['blank', 'error', 'hide'] as const).map(opt => (
                    <button
                      key={opt}
                      onClick={() => setOnMissing(opt)}
                      style={{
                        padding: '3px 10px',
                        border: `1px solid ${onMissing === opt ? colors.primary : colors.border}`,
                        borderRadius: radius.sm,
                        background: onMissing === opt ? colors.primary : 'none',
                        color: onMissing === opt ? colors.onPrimary : colors.text,
                        fontSize: fontSize.xs,
                        cursor: 'pointer',
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </label>
              <Toggle
                checked={watermark}
                onChange={setWatermark}
                label={<span style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>Add DOCMORPHIA TEST watermark</span>}
              />
            </div>
          </div>

          {/* Email delivery */}
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            padding: spacing.md,
            boxShadow: shadow.sm,
          }}>
            <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: spacing.sm }}>
              Send to Email
            </div>
            <div style={{ display: 'flex', gap: spacing.xs }}>
              <input
                type="email"
                value={emailTo}
                onChange={e => setEmailTo(e.target.value)}
                placeholder="recipient@example.com"
                onKeyDown={e => e.key === 'Enter' && !sending && emailTo.trim() && handleSendEmail()}
                style={{
                  flex: 1,
                  padding: '4px 8px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm,
                  fontSize: fontSize.sm,
                  outline: 'none',
                }}
              />
              <button
                onClick={handleSendEmail}
                disabled={sending || !emailTo.trim()}
                style={{
                  padding: '4px 12px',
                  background: emailTo.trim() && !sending ? colors.primary : colors.textMuted,
                  color: colors.onPrimary,
                  border: 'none',
                  borderRadius: radius.sm,
                  fontSize: fontSize.sm,
                  cursor: emailTo.trim() && !sending ? 'pointer' : 'default',
                  whiteSpace: 'nowrap',
                }}
              >
                {sending ? '…' : `Send ${outputFormat.toUpperCase()}`}
              </button>
            </div>
            {sendStatus === 'sent' && (
              <div style={{ fontSize: fontSize.sm, color: colors.success, marginTop: spacing.xs }}>
                Sent to {emailTo}
              </div>
            )}
            {sendStatus === 'error' && (
              <div style={{ fontSize: fontSize.sm, color: colors.error, marginTop: spacing.xs }}>
                Send failed. Check email address.
              </div>
            )}
          </div>

          {/* Deliver to URL (S3 pre-signed, Azure SAS, GCS signed, etc.) */}
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            padding: spacing.md,
            boxShadow: shadow.sm,
          }}>
            <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: spacing.xs }}>
              Deliver to URL
            </div>
            <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: spacing.sm }}>
              HTTP PUT to any pre-signed URL (S3, Azure Blob SAS, GCS signed URL, etc.)
            </div>
            <div style={{ display: 'flex', gap: spacing.xs }}>
              <input
                type="url"
                value={deliverToUrl}
                onChange={e => setDeliverToUrl(e.target.value)}
                placeholder="https://bucket.s3.amazonaws.com/key?X-Amz-Signature=…"
                onKeyDown={e => e.key === 'Enter' && !delivering && deliverToUrl.trim() && handleDeliver()}
                style={{
                  flex: 1,
                  padding: '4px 8px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm,
                  fontSize: fontSize.sm,
                  outline: 'none',
                  minWidth: 0,
                }}
              />
              <button
                onClick={handleDeliver}
                disabled={delivering || !deliverToUrl.trim()}
                style={{
                  padding: '4px 12px',
                  background: deliverToUrl.trim() && !delivering ? colors.primary : colors.textMuted,
                  color: colors.onPrimary,
                  border: 'none',
                  borderRadius: radius.sm,
                  fontSize: fontSize.sm,
                  cursor: deliverToUrl.trim() && !delivering ? 'pointer' : 'default',
                  whiteSpace: 'nowrap',
                }}
              >
                {delivering ? '…' : 'Deliver'}
              </button>
            </div>
            {deliverStatus === 'sent' && (
              <div style={{ fontSize: fontSize.sm, color: colors.success, marginTop: spacing.xs }}>
                Delivered successfully.
              </div>
            )}
            {deliverStatus === 'error' && (
              <div style={{ fontSize: fontSize.sm, color: colors.error, marginTop: spacing.xs }}>
                Delivery failed. Check the URL and try again.
              </div>
            )}
          </div>

          {/* Deliver to cloud storage */}
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            padding: spacing.md,
            boxShadow: shadow.sm,
          }}>
            <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: spacing.xs }}>
              Deliver to Cloud Storage
            </div>
            <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: spacing.sm }}>
              Upload to Google Drive via a connected account.
            </div>
            {cloudConnections.length === 0 ? (
              <div style={{ fontSize: fontSize.sm, color: colors.textMuted }}>
                No cloud storage accounts connected. <a href="/app/integrations" style={{ color: colors.primary }}>Connect Google Drive</a> first.
              </div>
            ) : (
              <div style={{ display: 'grid', gap: spacing.xs }}>
                <Select
                  inputSize="sm"
                  placeholder="Select account…"
                  value={cloudConnectionId}
                  onChange={v => setCloudConnectionId(String(v))}
                  options={cloudConnections.map(c => ({ value: c.oAuthConnectionId, label: c.accountLabel }))}
                />
                <input
                  type="text"
                  value={cloudFolderId}
                  onChange={e => setCloudFolderId(e.target.value)}
                  placeholder="Folder ID (optional)"
                  style={{ padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, background: colors.background, color: colors.text }}
                />
                <input
                  type="text"
                  value={cloudFileName}
                  onChange={e => setCloudFileName(e.target.value)}
                  placeholder={`File name (default: output.${outputFormat})`}
                  style={{ padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, background: colors.background, color: colors.text }}
                />
                <button
                  onClick={handleCloudDeliver}
                  disabled={cloudDelivering || !cloudConnectionId}
                  style={{
                    padding: '4px 12px',
                    background: cloudConnectionId && !cloudDelivering ? colors.primary : colors.textMuted,
                    color: colors.onPrimary,
                    border: 'none',
                    borderRadius: radius.sm,
                    fontSize: fontSize.sm,
                    cursor: cloudConnectionId && !cloudDelivering ? 'pointer' : 'default',
                    alignSelf: 'flex-start',
                  }}
                >
                  {cloudDelivering ? '…' : 'Upload'}
                </button>
                {cloudDeliverStatus === 'sent' && (
                  <div style={{ fontSize: fontSize.sm, color: colors.success }}>
                    Uploaded.{cloudResultUrl && <> <a href={cloudResultUrl} target="_blank" rel="noreferrer" style={{ color: colors.primary }}>Open in Drive</a></>}
                  </div>
                )}
                {cloudDeliverStatus === 'error' && (
                  <div style={{ fontSize: fontSize.sm, color: colors.error }}>Upload failed.</div>
                )}
              </div>
            )}
          </div>

          {/* Batch CSV */}
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            padding: spacing.md,
            boxShadow: shadow.sm,
          }}>
            <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: spacing.xs }}>
              Batch CSV Render
            </div>
            <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: spacing.sm }}>
              Upload a CSV (headers = variable names, each row = one render).
            </div>
            <div style={{ display: 'flex', gap: spacing.xs, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{
                padding: '4px 10px',
                border: `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                fontSize: fontSize.sm,
                cursor: 'pointer',
                color: colors.text,
                whiteSpace: 'nowrap',
              }}>
                {csvFile ? csvFile.name : 'Choose CSV…'}
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={e => { setCsvFile(e.target.files?.[0] ?? null); setCsvResults(null) }}
                  style={{ display: 'none' }}
                />
              </label>
              <button
                onClick={handleBatchCsv}
                disabled={!csvFile || csvRunning}
                style={{
                  padding: '4px 12px',
                  background: csvFile && !csvRunning ? colors.primary : colors.textMuted,
                  color: colors.onPrimary,
                  border: 'none',
                  borderRadius: radius.sm,
                  fontSize: fontSize.sm,
                  cursor: csvFile && !csvRunning ? 'pointer' : 'default',
                  whiteSpace: 'nowrap',
                }}
              >
                {csvRunning ? 'Running…' : 'Run Batch'}
              </button>
              {csvResults && !csvRunning && (
                <span style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>
                  {csvResults.filter(r => r.success).length}/{csvResults.length} succeeded
                </span>
              )}
            </div>
            {csvResults && csvResults.length > 0 && (
              <div style={{ marginTop: spacing.sm, maxHeight: 160, overflowY: 'auto' }}>
                {csvResults.map((r, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.xs,
                    padding: '2px 0',
                    fontSize: fontSize.xs,
                    borderBottom: i < csvResults.length - 1 ? `1px solid ${colors.border}` : 'none',
                  }}>
                    <span style={{ color: r.success ? colors.success : colors.error, minWidth: 14 }}>
                      {r.success ? '✓' : '✗'}
                    </span>
                    {r.rowIndex >= 0 && (
                      <span style={{ color: colors.textSecondary, minWidth: 50 }}>Row {r.rowIndex + 1}</span>
                    )}
                    {r.success ? (
                      <span style={{ fontFamily: 'monospace', color: colors.textMuted }}>{r.renderJobId.slice(0, 8)}…</span>
                    ) : (
                      <span style={{ color: colors.error }}>{r.errorMessage}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Batch XLSX */}
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            padding: spacing.md,
            boxShadow: shadow.sm,
          }}>
            <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: spacing.xs }}>
              Batch XLSX Render
            </div>
            <div style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: spacing.sm }}>
              Upload an Excel file (row 1 = headers, each subsequent row = one render).
            </div>
            <div style={{ display: 'flex', gap: spacing.xs, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{
                padding: '4px 10px',
                border: `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                fontSize: fontSize.sm,
                cursor: 'pointer',
                color: colors.text,
                whiteSpace: 'nowrap',
              }}>
                {xlsxFile ? xlsxFile.name : 'Choose XLSX…'}
                <input
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={e => { setXlsxFile(e.target.files?.[0] ?? null); setXlsxResults(null) }}
                  style={{ display: 'none' }}
                />
              </label>
              <button
                onClick={handleBatchXlsx}
                disabled={!xlsxFile || xlsxRunning}
                style={{
                  padding: '4px 12px',
                  background: xlsxFile && !xlsxRunning ? colors.primary : colors.textMuted,
                  color: colors.onPrimary,
                  border: 'none',
                  borderRadius: radius.sm,
                  fontSize: fontSize.sm,
                  cursor: xlsxFile && !xlsxRunning ? 'pointer' : 'default',
                  whiteSpace: 'nowrap',
                }}
              >
                {xlsxRunning ? 'Running…' : 'Run Batch'}
              </button>
              {xlsxResults && !xlsxRunning && (
                <span style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>
                  {xlsxResults.filter(r => r.success).length}/{xlsxResults.length} succeeded
                </span>
              )}
            </div>
            {xlsxResults && xlsxResults.length > 0 && (
              <div style={{ marginTop: spacing.sm, maxHeight: 160, overflowY: 'auto' }}>
                {xlsxResults.map((r, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.xs,
                    padding: '2px 0',
                    fontSize: fontSize.xs,
                    borderBottom: i < xlsxResults.length - 1 ? `1px solid ${colors.border}` : 'none',
                  }}>
                    <span style={{ color: r.success ? colors.success : colors.error, minWidth: 14 }}>
                      {r.success ? '✓' : '✗'}
                    </span>
                    {r.rowIndex >= 0 && (
                      <span style={{ color: colors.textSecondary, minWidth: 50 }}>Row {r.rowIndex + 1}</span>
                    )}
                    {r.success ? (
                      <span style={{ fontFamily: 'monospace', color: colors.textMuted }}>{r.renderJobId.slice(0, 8)}…</span>
                    ) : (
                      <span style={{ color: colors.error }}>{r.errorMessage}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Preview panel */}
        <div style={{
          display: isMobile && activeTab !== 'preview' ? 'none' : 'flex',
          flexDirection: 'column',
          minHeight: isMobile ? 500 : 0,
        }}>
          <PreviewPane pdfUrl={pdfUrl} loading={pdfLoading} error={pdfError} />
        </div>
      </div>

      <ConfirmModal
        open={pendingDelete !== null}
        title="Delete sample"
        message={<>Delete the saved sample <strong>{pendingDelete?.name}</strong>? This cannot be undone.</>}
        confirmLabel="Delete"
        danger
        loading={deleting}
        onConfirm={confirmDeleteSample}
        onCancel={() => setPendingDelete(null)}
      />

      {shareModalOpen && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setShareModalOpen(false) }}
          style={{
            position: 'fixed', inset: 0, background: colors.overlay,
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            padding: spacing.lg,
            width: 380,
            boxShadow: shadow.sm,
          }}>
            <div style={{ fontSize: fontSize.base, fontWeight: 700, color: colors.text, marginBottom: spacing.md }}>
              Share Preview
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
              <label style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>
                Expires after (hours)
                <input
                  type="number"
                  min={1} max={168}
                  value={shareTtl}
                  onChange={e => setShareTtl(Number(e.target.value))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, boxSizing: 'border-box' }}
                />
              </label>
              <label style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>
                Password (optional)
                <input
                  type="password"
                  value={sharePassword}
                  onChange={e => setSharePassword(e.target.value)}
                  placeholder="Leave blank for no password"
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, boxSizing: 'border-box' }}
                />
              </label>
              <label style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>
                Max views (optional)
                <input
                  type="number"
                  min={1}
                  value={shareMaxViews}
                  onChange={e => setShareMaxViews(e.target.value)}
                  placeholder="Unlimited"
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, boxSizing: 'border-box' }}
                />
              </label>
              {shareUrl ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                  <div style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>Share link:</div>
                  <div style={{ display: 'flex', gap: spacing.xs }}>
                    <input
                      readOnly
                      value={shareUrl}
                      onClick={e => (e.target as HTMLInputElement).select()}
                      style={{ flex: 1, padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.xs, fontFamily: 'monospace', minWidth: 0 }}
                    />
                    <button
                      onClick={() => navigator.clipboard.writeText(shareUrl)}
                      style={{ padding: '4px 10px', background: colors.primary, color: colors.onPrimary, border: 'none', borderRadius: radius.sm, fontSize: fontSize.sm, cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleSharePreview}
                  disabled={sharing}
                  style={{
                    padding: '6px 16px',
                    background: sharing ? colors.textMuted : colors.primary,
                    color: colors.onPrimary,
                    border: 'none',
                    borderRadius: radius.sm,
                    fontSize: fontSize.sm,
                    fontWeight: 600,
                    cursor: sharing ? 'default' : 'pointer',
                    marginTop: spacing.xs,
                  }}
                >
                  {sharing ? 'Creating…' : 'Create Link'}
                </button>
              )}
              <button
                onClick={() => setShareModalOpen(false)}
                style={{ background: 'none', border: 'none', color: colors.textSecondary, fontSize: fontSize.sm, cursor: 'pointer', textAlign: 'left', padding: 0 }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
      {dialogs.element}
    </>
  )
}
